// src/Admin/AITesting/api.ts
import {
  CompareResponse,
  TestInstruction,
  TestResultDB,
  TestText,
  TranslationResult,
  ModelResult,
  TestErrorEntry,
  ProductionRulesResponse,
} from './types'
import { resolveFrontendApiBase } from '../../utils/apiBase'
import adminAxios from '../adminAxios'

const BASE = resolveFrontendApiBase()

interface ManualTestResponse {
  translations: TranslationResult[]
  evaluations: ModelResult[]
  errors?: TestErrorEntry[]
}

export interface AITestingModelCatalogEntry {
  id: string
  displayName?: string
  provider?: string
  apiModel?: string
  inputCostPerMTokens?: number
  cachedInputCostPerMTokens?: number
  outputCostPerMTokens?: number
  isActive?: boolean
  testingOnly?: boolean
  internalOnly?: boolean
  tags?: string[]
}

export async function fetchInstructions(language: string): Promise<TestInstruction[]> {
  const res = await fetch(`${BASE}/api/test-instructions/${encodeURIComponent(language)}`)
  if (!res.ok) throw new Error('Failed to fetch instructions')
  return res.json()
}

export async function fetchTestTexts(): Promise<TestText[]> {
  const res = await fetch(`${BASE}/api/test-texts`)
  if (!res.ok) throw new Error('Failed to fetch test texts')
  return res.json()
}

export async function fetchDbResults(language: string): Promise<TestResultDB[]> {
  const res = await fetch(`${BASE}/api/test-results?language=${encodeURIComponent(language)}`)
  if (!res.ok) throw new Error('Failed to fetch results')
  return res.json()
}

export async function fetchAiTestingModelCatalog(
  includeInactive: boolean = true
): Promise<AITestingModelCatalogEntry[]> {
  const params = new URLSearchParams()
  params.append('bypass_cache', 'true')
  if (includeInactive) {
    params.append('includeInactive', 'true')
  }
  params.append('includeInternal', 'true')
  const url = `${BASE}/api/ai-models${params.toString() ? `?${params.toString()}` : ''}`
  const { data } = await adminAxios.get<AITestingModelCatalogEntry[]>(url)
  return data
}

export async function fetchDbResultsPaged(language: string, limit: number, offset: number): Promise<TestResultDB[]> {
  const url = `${BASE}/api/test-results?language=${encodeURIComponent(language)}&limit=${limit}&offset=${offset}`
  const res = await fetch(url)
  if (!res.ok) throw new Error('Failed to fetch paged results')
  return res.json()
}

export async function fetchDbResultsCount(language: string): Promise<number> {
  const url = `${BASE}/api/test-results/count?language=${encodeURIComponent(language)}`
  const res = await fetch(url)
  if (!res.ok) throw new Error('Failed to fetch result count')
  const data = await res.json()
  return data?.total ?? 0
}

// Server-side aggregates (use ALL test results)
export async function fetchAggregatedScores(
  language: string,
  instructionId?: string,
  testName?: string,
  batchId?: string
) {
  const params = new URLSearchParams({ language })
  if (instructionId) params.append('instructionId', instructionId)
  if (testName) params.append('testName', testName)
  if (batchId) params.append('batchId', batchId)
  const res = await fetch(`${BASE}/api/test-results/aggregates/scores?${params.toString()}`)
  if (!res.ok) throw new Error('Failed to fetch aggregated scores')
  return res.json()
}

export async function fetchAggregatedEvalTokens(
  language: string,
  instructionId?: string,
  testName?: string,
  batchId?: string
) {
  const params = new URLSearchParams({ language })
  if (instructionId) params.append('instructionId', instructionId)
  if (testName) params.append('testName', testName)
  if (batchId) params.append('batchId', batchId)
  const res = await fetch(`${BASE}/api/test-results/aggregates/eval-tokens?${params.toString()}`)
  if (!res.ok) throw new Error('Failed to fetch aggregated eval tokens')
  return res.json()
}

export async function fetchAggregatedInstructions(
  language: string,
  testName?: string,
  batchId?: string
): Promise<Array<{ id: string; name: string; count: number }>> {
  const params = new URLSearchParams({ language })
  if (testName) params.append('testName', testName)
  if (batchId) params.append('batchId', batchId)
  const res = await fetch(`${BASE}/api/test-results/aggregates/instructions?${params.toString()}`)
  if (!res.ok) throw new Error('Failed to fetch aggregated instruction options')
  return res.json()
}

export async function fetchAggregatedTestNames(
  language: string,
  instructionId?: string,
  batchId?: string
): Promise<Array<{ name: string; count: number }>> {
  const params = new URLSearchParams({ language })
  if (instructionId) params.append('instructionId', instructionId)
  if (batchId) params.append('batchId', batchId)
  const res = await fetch(`${BASE}/api/test-results/aggregates/test-names?${params.toString()}`)
  if (!res.ok) throw new Error('Failed to fetch aggregated test-name options')
  return res.json()
}

export async function fetchAggregatedBatches(
  language: string,
  instructionId?: string,
  testName?: string
): Promise<Array<{ id: string; name: string; count: number; latestCreatedAt?: string }>> {
  const params = new URLSearchParams({ language })
  if (instructionId) params.append('instructionId', instructionId)
  if (testName) params.append('testName', testName)
  const res = await fetch(`${BASE}/api/test-results/aggregates/batches?${params.toString()}`)
  if (!res.ok) throw new Error('Failed to fetch aggregated batch options')
  return res.json()
}

export async function fetchAggregatedFilterOptions(
  language: string,
  instructionId?: string,
  testName?: string,
  batchId?: string
): Promise<{
  instructions: Array<{ id: string; name: string; count: number }>
  testNames: Array<{ name: string; count: number }>
  batches: Array<{ id: string; name: string; count: number; latestCreatedAt?: string }>
}> {
  const params = new URLSearchParams({ language })
  if (instructionId) params.append('instructionId', instructionId)
  if (testName) params.append('testName', testName)
  if (batchId) params.append('batchId', batchId)
  const res = await fetch(`${BASE}/api/test-results/aggregates/filter-options?${params.toString()}`)
  if (!res.ok) throw new Error('Failed to fetch aggregate filter options')
  return res.json()
}

export async function deleteSavedResult(id: string): Promise<void> {
  const res = await fetch(`${BASE}/api/test-results/${id}`, { method: 'DELETE' })
  if (!res.ok) throw new Error('Failed to delete')
}

export async function updateDiscardFlags(resultId: string, discarded: string[]): Promise<void> {
  const res = await fetch(`${BASE}/api/test-results/${resultId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ discarded_evaluations: discarded }),
  })
  if (!res.ok) {
    const err = await res.text()
    throw new Error(err || 'Failed to update discard flags')
  }
}

export async function saveInstruction(
  payload: Omit<TestInstruction, 'id' | 'created_at' | 'updated_at'>
): Promise<void> {
  const res = await fetch(`${BASE}/api/test-instructions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error('Failed to save instruction')
}

export async function updateInstruction(
  id: string,
  payload: Partial<Omit<TestInstruction, 'id'>>
): Promise<void> {
  const res = await fetch(`${BASE}/api/test-instructions/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error('Failed to update instruction')
}

export async function deleteInstruction(id: string): Promise<void> {
  const res = await fetch(`${BASE}/api/test-instructions/${id}`, { method: 'DELETE' })
  if (!res.ok) throw new Error('Failed to delete instruction')
}

export async function saveText(
  payload: Omit<TestText, 'id' | 'created_at' | 'updated_at'>
): Promise<void> {
  const res = await fetch(`${BASE}/api/test-texts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error('Failed to save text')
}

export async function updateText(
  id: string,
  payload: Partial<Omit<TestText, 'id'>>
): Promise<void> {
  const res = await fetch(`${BASE}/api/test-texts/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error('Failed to update text')
}

export async function deleteText(id: string): Promise<void> {
  const res = await fetch(`${BASE}/api/test-texts/${id}`, { method: 'DELETE' })
  if (!res.ok) throw new Error('Failed to delete text')
}

export async function runManualTest(payload: {
  system_msg: string
  text_to_translate: string
  target_language: string
  models: string[]
  evaluation_models?: string[]
  french_em_dash_enforce?: boolean
  french_em_dash_instructions?: string
  testing?: boolean
}): Promise<ManualTestResponse> {
  const { data } = await adminAxios.post<ManualTestResponse>(
    `${BASE}/ai-testing`,
    payload,
  )
  return data
}

export async function fetchProductionRules(payload: {
  target_language: string
  source_language?: string
  genre?: string
  quotation_style?: string
  api_model?: string
  intended_audience?: string
  tone?: string
}): Promise<ProductionRulesResponse> {
  const resp = await fetch(`${BASE}/api/test-production-rules`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!resp.ok) {
    const err = await resp.text()
    throw new Error(err || 'Failed to fetch production rules')
  }
  return resp.json()
}


export async function compareTranslations(payload: {
  original_text: string
  model1: string
  translation1: string
  model2: string
  translation2: string
}): Promise<CompareResponse> {
  const resp = await fetch(`${BASE}/compare-translations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!resp.ok) {
    const errText = await resp.text()
    throw new Error(errText || 'Unknown comparison error')
  }
  return resp.json()
}

export async function deleteResultsByLanguage(
  language: string,
  instructionId?: string,
  batchId?: string,
  dryRun: boolean = false
): Promise<{ success: boolean; deleted: number; matched: number; language: string; instructionId?: string; batchId?: string; dry_run?: boolean }> {
  const params = new URLSearchParams({ language })
  if (instructionId) params.append('instructionId', instructionId)
  if (batchId) params.append('batchId', batchId)
  if (dryRun) params.append('dry_run', 'true')
  const resp = await fetch(`${BASE}/api/test-results?${params.toString()}`, {
    method: 'DELETE',
  })
  if (!resp.ok) {
    const errText = await resp.text()
    throw new Error(errText || 'Failed to bulk delete results')
  }
  return resp.json()
}
